import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DisclosureSettings } from '../../../../../model/disclosure-settings.interface';
import { DisclosureSetOptionsService, DisclosureSetSelectOption } from '../../../../../service/disclosure-set-options.service';

@Component({
  selector: 'auto-advanced-disclosures',
  templateUrl: './disclosures.component.html',
  styleUrls: ['./disclosures.component.scss'],
  standalone: false
})
export class AdvancedDisclosuresComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly DEFAULT_DISCLOSURE_SET_ID = 1;

  @Input() data: DisclosureSettings | null = null;
  @Output() dataEmitter = new EventEmitter<UntypedFormGroup>();
  form: UntypedFormGroup;
  disclosureSetOptions: DisclosureSetSelectOption[] = [];
  disclosureSetTooltip = '';
  private advancedSettings: DisclosureSettings | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private disclosureSetOptionsService: DisclosureSetOptionsService
  ) {}

  ngOnInit(): void {
    this.form = this.buildForm();
    this.registerFormSubscriptions();
    this.dataEmitter.emit(this.form);
    this.patchFromInput(this.data);
  }

  /**
   * Builds and returns the reactive form group with all disclosure fields and their default values.
   */
  private buildForm(): UntypedFormGroup
  {
    return this.formBuilder.group({
      // Advanced
      enhancedDisclosure: [false],
      disclosureSet: [0],
      // New Ad Defaults
      newShowDealInfoInDisclaimer: [false],
      newShowDaysToFirstPaymentInDisclosure: [false],
      newFinanceDisclosure: [''],
      newBuyDisclosure: [''],
      newAprDisclosure: [''],
      newLeaseDisclosure: [''],
      newAlternateLeaseDisclosure: [''],
      newDisclosureAddon: [''],
      newShowPhotosForIllustration: [false],
      newShowAllAvailableStocks: [false],
      newShowTotalAvailableStocks: [false],
      newShowLastEightVin: [false],
      newShowPricingSuperscripts: [false],
      // Used Ad Defaults
      usedFinanceDisclosure: [''],
      usedShowDaysToFirstPaymentInDisclosure: [false],
      usedBuyDisclosure: [''],
      usedAprDisclosure: [''],
      usedSavingsDisclosure: [''],
      usedShowDealsInDisclaimers: [false],
      usedLeaseDisclosure: [''],
      usedAlternateLeaseDisclosure: [''],
      usedDisclosureAddon: [''],
      usedShowPhotosForIllustration: [false],
      usedShowLastEightVin: [false],
      usedShowPricingSuperscripts: [false]
    });
  }

  /**
   * Subscribes to enhancedDisclosure and disclosureSet value changes for the lifetime of the component.
   */
  private registerFormSubscriptions(): void
  {
    this.form.get('enhancedDisclosure')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.onEnhancedDisclosureChanged(enabled);
      });

    this.form.get('disclosureSet')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDisclosureSetTooltip();
      });
  }

  /**
   * Re-patches the form whenever the parent passes a new data input.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.patchFromInput(changes['data'].currentValue);
    }
  }

  /**
   * Completes the destroy subject to unsubscribe all reactive subscriptions.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Rebuilds the available disclosure set options when the enhanced disclosure toggle changes,
   * resets the selected set to the default if the current selection is no longer valid,
   * and refreshes the tooltip.
   */
  private onEnhancedDisclosureChanged(enabled: boolean): void {
    if (this.advancedSettings) {
      this.advancedSettings = { ...this.advancedSettings, enhancedDisclosure: enabled };
      this.disclosureSetOptions = this.buildDisclosureSetOptions(this.advancedSettings);
    }

    const currentValue = this.form.get('disclosureSet')?.value;
    const exists = this.disclosureSetOptions.some(option => option.value === currentValue);

    if (!exists) {
      this.form.patchValue(
        { disclosureSet: AdvancedDisclosuresComponent.DEFAULT_DISCLOSURE_SET_ID },
        { emitEvent: false }
      );
    }

    this.updateDisclosureSetTooltip();
  }

  /**
   * Stores the incoming settings, rebuilds disclosure set options, patches all form controls,
   * updates the tooltip, and marks the form pristine.
   */
  private patchFromInput(disclosureSettings: DisclosureSettings | null): void {
    if (disclosureSettings && this.form) {
      this.advancedSettings = disclosureSettings;
      this.disclosureSetOptions = this.buildDisclosureSetOptions(disclosureSettings);
      this.form.patchValue(this.buildPatchValue(disclosureSettings), { emitEvent: false });
      this.updateDisclosureSetTooltip();
      this.form.markAsPristine();
    }
  }

  /**
   * Combines the advanced, new-ad, and used-ad patch objects into a single form patch value.
   */
  private buildPatchValue(disclosureSettings: DisclosureSettings): object {
    return {
      ...this.buildAdvancedPatchValue(disclosureSettings),
      ...this.buildNewPatchValue(disclosureSettings),
      ...this.buildUsedPatchValue(disclosureSettings)
    };
  }

  /**
   * Returns the patch value for the advanced section (enhancedDisclosure and disclosureSet),
   * defaulting disclosureSet to 0 when the incoming value is not a finite number.
   */
  private buildAdvancedPatchValue(disclosureSettings: DisclosureSettings): object {
    let selectedDisclosureSet = 0;

    if (Number.isFinite(disclosureSettings.disclosureSet)) {
      selectedDisclosureSet = Number(disclosureSettings.disclosureSet);
    }

    return {
      enhancedDisclosure: !!disclosureSettings.enhancedDisclosure,
      disclosureSet: selectedDisclosureSet
    };
  }

  /**
   * Delegates to DisclosureSetOptionsService to build the filtered and mapped dropdown options.
   */
  private buildDisclosureSetOptions(disclosureSettings: DisclosureSettings): DisclosureSetSelectOption[] {
    return this.disclosureSetOptionsService.buildDisclosureSetOptions(disclosureSettings);
  }

  /**
   * Delegates to DisclosureSetOptionsService to compute and set the tooltip for the selected disclosure set.
   */
  private updateDisclosureSetTooltip(): void {
    this.disclosureSetOptionsService.updateDisclosureSetTooltip(
      this.form,
      this.disclosureSetOptions,
      (tooltip) => { this.disclosureSetTooltip = tooltip; }
    );
  }

  /**
   * Returns the patch value for all new-vehicle ad disclosure fields.
   */
  private buildNewPatchValue(disclosureSettings: DisclosureSettings): object {
    return {
      newShowDealInfoInDisclaimer: !!disclosureSettings.newShowDealInfoInDisclaimer,
      newShowDaysToFirstPaymentInDisclosure: !!disclosureSettings.newShowDaysToFirstPaymentInDisclosure,
      newFinanceDisclosure: disclosureSettings.newFinanceDisclosure || '',
      newBuyDisclosure: disclosureSettings.newBuyDisclosure || '',
      newAprDisclosure: disclosureSettings.newAprDisclosure || '',
      newLeaseDisclosure: disclosureSettings.newLeaseDisclosure || '',
      newAlternateLeaseDisclosure: disclosureSettings.newAlternateLeaseDisclosure || '',
      newDisclosureAddon: disclosureSettings.newDisclosureAddon || '',
      newShowPhotosForIllustration: !!disclosureSettings.newShowPhotosForIllustration,
      newShowAllAvailableStocks: !!disclosureSettings.newShowAllAvailableStocks,
      newShowTotalAvailableStocks: !!disclosureSettings.newShowTotalAvailableStocks,
      newShowLastEightVin: !!disclosureSettings.newShowLastEightVin,
      newShowPricingSuperscripts: !!disclosureSettings.newShowPricingSuperscripts
    };
  }

  /**
   * Returns the patch value for all used-vehicle ad disclosure fields.
   */
  private buildUsedPatchValue(disclosureSettings: DisclosureSettings): object {
    return {
      usedFinanceDisclosure: disclosureSettings.usedFinanceDisclosure || '',
      usedShowDaysToFirstPaymentInDisclosure: !!disclosureSettings.usedShowDaysToFirstPaymentInDisclosure,
      usedBuyDisclosure: disclosureSettings.usedBuyDisclosure || '',
      usedAprDisclosure: disclosureSettings.usedAprDisclosure || '',
      usedSavingsDisclosure: disclosureSettings.usedSavingsDisclosure || '',
      usedShowDealsInDisclaimers: !!disclosureSettings.usedShowDealsInDisclaimers,
      usedLeaseDisclosure: disclosureSettings.usedLeaseDisclosure || '',
      usedAlternateLeaseDisclosure: disclosureSettings.usedAlternateLeaseDisclosure || '',
      usedDisclosureAddon: disclosureSettings.usedDisclosureAddon || '',
      usedShowPhotosForIllustration: !!disclosureSettings.usedShowPhotosForIllustration,
      usedShowLastEightVin: !!disclosureSettings.usedShowLastEightVin,
      usedShowPricingSuperscripts: !!disclosureSettings.usedShowPricingSuperscripts
    };
  }
}
